<script>
  // Import Svelte
  import { createEventDispatcher } from "svelte";
  import { onMount } from "svelte";
  import { log, announce } from "./Logging.svelte";
  import { callActive, cameraActive } from "../callState.js";
  import { isNative } from "../native.js";

  // Properties
  export let prefs;
  export let status = [];

  // Dispatch events
  const dispatch = createEventDispatcher();

  // Event functions
  function openConfig() {
    dispatch("openConfig", {
      action: ""
    });
  }

  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      try {
        document.body.requestFullscreen();
      } catch {
        console.log(`Error attempting to enable full-screen mode`);
      }
    } else {
      document.exitFullscreen();
    }
  }

  function toggleDebug() {
    prefs.debug = !prefs.debug;
  }

  function signOut() {
    dispatch("signOut");
  }

  onMount(()=>{
    announce("Navigation Mounted");
  });
</script>

<style>
  .indicators {
    position: fixed;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100002;
    display: flex;
    gap: 6px;
    pointer-events: none;
  }
  .indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(21, 16, 14, 0.55);
    color: rgba(245, 233, 214, 0.9);
    font: 500 12px/1.4 Inter, system-ui, sans-serif;
    letter-spacing: 0.02em;
  }
  .indicator__dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  .indicator__dot--camera { background: #e0483f; box-shadow: 0 0 6px #e0483f; }
  .indicator__dot--window { background: #4fc26b; box-shadow: 0 0 6px #4fc26b; }

  /* Quiet controls in the bottom-right corner of the window page. */
  .actions {
    position: fixed;
    right: 14px;
    bottom: 12px;
    z-index: 100002;
    display: flex;
    gap: 6px;
  }
  .actions__btn {
    border: 1px solid rgba(245, 233, 214, 0.25);
    border-radius: 999px;
    padding: 4px 12px;
    background: rgba(21, 16, 14, 0.55);
    color: rgba(245, 233, 214, 0.75);
    font: 500 12px/1.4 Inter, system-ui, sans-serif;
    cursor: pointer;
  }
  .actions__btn:hover { color: #f5e9d6; border-color: rgba(245, 233, 214, 0.6); }

  .button.is-text {
    text-decoration: none;
  }
  .card {
    background-color: rgba(0, 0, 0, 0.4);
    color: white;
  }
  .is-overlay {
    z-index: 100;
  }
  .align-bottom {
    margin-top: auto;
  }
  .no-padding {
    padding: 0px;
  }
  .padding-5 {
    padding: 5px;
  }
  .debug-card {
    background: rgba(25, 25, 25, 0.8);
    border-radius: 4px;
    color: #ffffff;
    margin: 10px;
    min-width: 300px;
  }
  .statusRow {
    display: flex;
  }
  .statusColumn {
    flex: 50%;
  }
</style>

<!-- Always visible while the camera or a window is in use; cannot be dismissed. -->
<div class="indicators" aria-live="polite">
  {#if $cameraActive}<span class="indicator"><span class="indicator__dot indicator__dot--camera" />Camera on</span>{/if}
  {#if $callActive}<span class="indicator"><span class="indicator__dot indicator__dot--window" />Window open</span>{/if}
</div>
<!-- The iOS app draws its own Friends and Settings buttons, so these are web only. -->
{#if !isNative}
  <div class="actions">
    <button type="button" class="actions__btn" on:click={openConfig}>Settings</button>
    <button type="button" class="actions__btn" on:click={signOut}>Sign out</button>
  </div>
{/if}
<div class="hero is-overlay is-fullheight is-widescreen">
  <!-- Top Left | Stats and Stats Show/Hide-->
  <div class="section no-padding">
    {#if prefs.debug}
      <div class="is-pulled-left">
        <div class="card debug-card" on:click={toggleDebug}>
          <div class="card-content padding-5">
            <div class="statusRow">
              <div class="statusColumn has-text-right padding-5">
                <ul>
                  {#each status as stat, i}
                    <li class="is-size-7 has-text-light has-text-weight-bold">
                      {stat.name}
                    </li>
                  {/each}
                </ul>
              </div>
              <div class="statusColumn padding-5">
                <ul>
                  {#each status as stat, i}
                    {#if stat.name == 'Detection Timer'}
                      <li class="is-size-7 has-text-light">{Math.round(stat.value/1000)} of {Math.round(prefs.faceDetectionSlack/1000)}</li>
                    {:else}
                      <li class="is-size-7 has-text-light">{stat.value}</li>
                    {/if}
                  {/each}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    {:else}
      <button class="button is-text is-pulled-left" on:click={toggleDebug}>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      </button>
    {/if}
    <!-- Top Right | Open Config-->
    {#if prefs.debug}
      <button
        class="button is-info is-inverted is-outlined is-pulled-right
        has-text-light"
        on:click={openConfig}>
        <span class="icon is-medium">
          <i class="fas fa-cog" />
        </span>
      </button>
    {:else}
      <button class="button is-text is-pulled-right" on:click={openConfig}>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      </button>
    {/if}
  </div>
  <!-- Bottom Left Fullscreen-->
  <div class="is-pulled-right align-bottom">
    {#if prefs.debug}
      <button
        class="button is-info is-inverted is-outlined is-pulled-right
        has-text-light"
        on:click={toggleFullscreen}>
        <span class="icon is-medium">
          <i class="fas fa-expand" />
        </span>
      </button>
    {:else}
      <button
        class="button is-text is-pulled-right"
        on:click={toggleFullscreen}>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      </button>
    {/if}
  </div>
</div>
